import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowRightLeft,
  BriefcaseBusiness,
  CheckCircle2,
  FileText,
  LoaderCircle,
  RefreshCw,
  ShieldAlert,
  Building2,
  Landmark,
  Users,
  Handshake,
  Home,
  RefreshCcw,
  AlertCircle,
  User,
  Play,
  Gavel
} from 'lucide-react';
import { useAuth } from '../../context/AuthProvider';
import Tooltip from '../../components/ui/Tooltip';
import { getDossierById, updateDossier, convertDossier } from '../../features/forum/api/forumAPI';
import StatusBadge from '../../components/status/StatusBadge';
import RiskBadge from '../../components/status/RiskBadge';
import { OPERATION_TYPES_LABELS } from '../../constants/operationTypes';
import { DOSSIER_STATUSES_LABELS } from '../../constants/dossierStatuses';
import { RISK_LEVELS_LABELS } from '../../constants/riskLevels';
import CustomSelect from '../../components/ui/CustomSelect';

import DossierOverview from '../../features/forum/components/tabs/DossierOverview';
import DossierParticipants from '../../features/forum/components/tabs/DossierParticipants';
import DossierDocuments from '../../features/forum/components/tabs/DossierDocuments';
import DossierAnalyses from '../../features/forum/components/tabs/DossierAnalyses';
import DossierTimeline from '../../features/forum/components/tabs/DossierTimeline';
import DossierDecision from '../../features/forum/components/tabs/DossierDecision';
import AlertModal from '../../components/ui/AlertModal';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { useNotification } from '../../context/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
import ResourceAccess from '../../components/permissions/ResourceAccess';

import MiniLoader from '../../components/MiniLoader';
import NotFound from '../errors/NotFound';
import Unauthorized from '../errors/Unauthorized';

export default function DossierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useNotification();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isConverting, setIsConverting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [isConfirmConvertOpen, setIsConfirmConvertOpen] = useState(false);
  const [decisionModalState, setDecisionModalState] = useState({ isOpen: false, initialType: null });
  const [convertedTransactionId, setConvertedTransactionId] = useState(null);

  const fetchDossier = async () => {
    try {
      const data = await getDossierById(id);
      setDossier(data);
    } catch (error) {
      console.error("Erreur lors de la récupération du dossier", error);
      setErrorStatus(error.response?.status || 500);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDossier();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    setStatusLoading(true);
    try {
      await updateDossier(id, { status: newStatus });
      showToast("Statut du dossier mis à jour avec succès", "success");
      fetchDossier();
    } catch (error) {
      showToast("Erreur lors de la mise à jour du statut", "error");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleRiskChange = async (newRisk) => {
    try {
      await updateDossier(id, { riskLevel: newRisk });
      showToast("Niveau de risque mis à jour avec succès", "success");
      fetchDossier();
    } catch (error) {
      showToast("Erreur lors de la mise à jour du risque", "error");
    }
  };

  const handleConvert = async () => {
    setIsConfirmConvertOpen(false);
    setIsConverting(true);
    try {
      const transaction = await convertDossier(id);
      showToast(`Dossier converti en transaction ${transaction.reference} avec succès !`, "success");
      setConvertedTransactionId(transaction.id);
      fetchDossier();
    } catch (error) {
      showToast(error.response?.data?.error || "Erreur lors de la conversion", "error");
    } finally {
      setIsConverting(false);
    }
  };

  if (loading) return <MiniLoader />;
  if (errorStatus === 401 || errorStatus === 403) return <Unauthorized />;
  if (!dossier || errorStatus === 404) return <NotFound />;

  const getOperationIcon = (type) => {
    switch (type) {
      case 'REAL_ESTATE_CLUB_DEAL': return Building2;
      case 'PRIVATE_FINANCING': return Landmark;
      case 'CROWDFUNDING': return Users;
      case 'VENDOR_LOAN': return Handshake;
      case 'REAL_ESTATE_CARRYING': return Home;
      case 'SALE_WITH_RIGHT_OF_REDEMPTION': return RefreshCcw;
      default: return BriefcaseBusiness;
    }
  };
  const OperationIcon = dossier ? getOperationIcon(dossier.operationType) : FileText;

  const timelineSteps = [
    { id: 'NEW', label: 'Réception' },
    { id: 'UNDER_ANALYSIS', label: 'Analyse' },
    { id: 'INVESTMENT_COMMITTEE', label: 'Comité' },
    { id: 'APPROVED', label: 'Décision' },
    { id: 'CONVERTED', label: 'Transaction' }
  ];

  let currentIndex = timelineSteps.findIndex(s => s.id === dossier.status);
  if (dossier.status === 'APPROVED_WITH_CONDITIONS' || dossier.status === 'REJECTED') {
    currentIndex = 3;
  } else if (currentIndex === -1) {
    currentIndex = 0;
  }

  const tabs = [
    { id: 'overview', name: 'Vue d\'ensemble' },
    { id: 'participants', name: `Intervenants (${dossier.participants?.length || 0})` },
    { id: 'documents', name: `Documents (${dossier.documents?.length || 0})` },
    { id: 'analyses', name: 'Analyses & Risques' },
    { id: 'decision', name: 'Décision' },
    { id: 'history', name: 'Historique' },
    { id: 'access', name: 'Accès' },
  ];

  const tabVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-4 sm:p-8 bg-workspace h-full"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
        <Breadcrumbs
          items={[
            { label: 'Forum', path: '/forum' },
            { label: dossier.title }
          ]}
        />

        <div className="flex flex-wrap items-center gap-3">
          {/* Statut */}
          <div className="flex items-center h-10 bg-surface rounded-xl border border-workspace shadow-sm focus-within:ring-2 focus-within:ring-[var(--hp-bronze-500-rgb)]/20 transition-all duration-300 group">
            <div className="flex items-center gap-2 px-3.5 h-full bg-surface-muted border-r border-workspace rounded-l-xl transition-colors group-hover:bg-workspace">
              <RefreshCw className="h-4 w-4 text-tertiary" strokeWidth={2} />
              <Tooltip content="Modification manuelle (mode dérogatoire)">
                <span className="text-xs font-bold uppercase tracking-wider text-tertiary hidden sm:block cursor-help border-b border-dashed border-tertiary">Statut</span>
              </Tooltip>
            </div>
            <div className="w-48 h-full flex items-center">
              <CustomSelect
                value={dossier.status}
                onChange={handleStatusChange}
                options={Object.entries(DOSSIER_STATUSES_LABELS).map(([key, label]) => ({
                  value: key,
                  label,
                }))}
                className="h-full w-full"
                triggerClassName="!border-0 !shadow-none !bg-transparent !h-full !min-h-0 !py-0 !px-3.5 flex items-center"
              />
            </div>
          </div>

          {/* Risque */}
          <div className="flex items-center h-10 bg-surface rounded-xl border border-workspace shadow-sm focus-within:ring-2 focus-within:ring-[var(--hp-bronze-500-rgb)]/20 transition-all duration-300 group">
            <div className="flex items-center gap-2 px-3.5 h-full bg-surface-muted border-r border-workspace rounded-l-xl transition-colors group-hover:bg-workspace">
              <ShieldAlert className="h-4 w-4 text-tertiary" strokeWidth={2} />
              <span className="text-xs font-bold uppercase tracking-wider text-tertiary hidden sm:block">Risque</span>
            </div>
            <div className="w-44 h-full flex items-center">
              <CustomSelect
                value={dossier.riskLevel || ''}
                onChange={handleRiskChange}
                options={Object.entries(RISK_LEVELS_LABELS).map(([key, label]) => ({
                  value: key,
                  label,
                }))}
                placeholder="Non évalué"
                className="h-full w-full"
                triggerClassName="!border-0 !shadow-none !bg-transparent !h-full !min-h-0 !py-0 !px-3.5 flex items-center"
              />
            </div>
          </div>



          {/* Converti */}
          {(dossier.convertedToTransaction || convertedTransactionId) && (
            <div className="flex items-center gap-2 h-10 rounded-xl border border-success-200 bg-success-50 px-4 text-sm font-semibold text-success-700 shadow-sm dark:border-success-800 dark:bg-success-900/40 dark:text-success-300">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
              <span>Converti en Opération</span>
              {(dossier.convertedTransactionId || dossier.transactionId || convertedTransactionId) && (
                <Link
                  to={`/transactions/${dossier.convertedTransactionId || dossier.transactionId || convertedTransactionId}`}
                  className="ml-2 flex items-center gap-1 text-xs font-bold uppercase text-success-800 hover:text-success-950 dark:text-success-200 underline"
                >
                  {dossier.convertedTransactionRef ? `#${dossier.convertedTransactionRef}` : 'Voir'} <ArrowRightLeft className="w-3 h-3" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="group relative mb-6 rounded-2xl border border-workspace bg-surface shadow-hp-md transition-all duration-300 hover:shadow-hp-lg">
        {/* Liseré supérieur */}
        <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-primary via-[var(--hp-ink-700)] to-secondary z-10" />

        {/* Conteneur pour les décorations afin qu'elles ne débordent pas de la carte */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div
            aria-hidden="true"
            className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[var(--hp-ink-900)]/5 blur-3xl transition-transform duration-700 group-hover:scale-125"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-32 left-1/3 h-56 w-56 rounded-full bg-secondary/10 blur-3xl"
          />
        </div>

        <div className="relative p-5 sm:p-6">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            {/* Informations du dossier */}
            <div className="min-w-0 flex-1 lg:pr-4">
              <div className="flex items-start gap-4">
                {/* Icône principale */}
                <div className="hidden h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-workspace bg-gradient-to-br from-[var(--hp-ink-100)] to-surface shadow-md sm:flex backdrop-blur-sm">
                  <OperationIcon className="h-8 w-8 text-primary" strokeWidth={1.5} />
                </div>

                <div className="min-w-0 flex-1">
                  {/* Type & Ref */}
                  <div className="flex items-center gap-2 mb-1.5 text-xs font-semibold tracking-wider uppercase">
                    <span className="text-tertiary flex items-center">
                      <BriefcaseBusiness className="w-3.5 h-3.5 mr-1.5" />
                      {dossier.reference}
                    </span>
                    <span className="text-tertiary">•</span>
                    <span className="text-secondary">{OPERATION_TYPES_LABELS[dossier.operationType] || dossier.operationType}</span>
                  </div>

                  {/* Titre */}
                  <h1 className="break-words text-2xl font-extrabold tracking-tight text-primary sm:text-3xl mb-3 leading-tight">
                    {dossier.title}
                  </h1>

                  {/* Badges */}
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={dossier.status} type="dossier" />
                    {dossier.riskLevel && <RiskBadge level={dossier.riskLevel} />}
                  </div>
                </div>
              </div>
            </div>

            {/* Responsable & Alertes (Nouveau bloc) */}
            <div className="w-full lg:w-auto xl:min-w-[280px] shrink-0 flex flex-col gap-3 lg:pr-4 border-t border-workspace pt-6 lg:border-t-0 lg:pt-0">
              <div className="bg-surface-muted/50 border border-workspace rounded-xl py-2 px-3 flex items-center gap-2.5 shadow-sm transition-colors hover:bg-surface-muted">
                <div className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-[var(--hp-info-100)] to-[var(--hp-info-300)] text-[var(--hp-info-700)] flex items-center justify-center font-bold text-xs shadow-sm ring-2 ring-workspace">
                  {dossier.assignedUser?.Nom ? dossier.assignedUser.Nom.charAt(0).toUpperCase() : <User className="w-4 h-4 opacity-75" />}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-tertiary font-semibold flex items-center gap-1 leading-none mb-0.5">
                    Responsable
                  </p>
                  <p className="text-sm font-bold text-primary truncate max-w-[200px] leading-none">
                    {dossier.assignedUser?.Nom || 'Non assigné'}
                  </p>
                </div>
              </div>

              {dossier.alerts && dossier.alerts.length > 0 && (
                <div className="bg-danger-50 border border-danger-200 rounded-xl p-3 flex flex-col gap-1.5 shadow-sm relative overflow-hidden group/alert">
                  <div className="absolute top-0 right-0 w-12 h-12 bg-danger-500/10 rounded-bl-full -mr-2 -mt-2 transition-transform group-hover/alert:scale-110" />
                  <div className="flex items-center gap-2 relative z-10">
                    <AlertCircle className="w-4 h-4 text-danger-600 shrink-0" strokeWidth={2.5} />
                    <h4 className="text-xs font-bold text-danger-800 uppercase tracking-wider">Attention requise</h4>
                  </div>
                  <ul className="text-xs text-danger-700 list-disc list-inside space-y-0.5 relative z-10">
                    {dossier.alerts.map((alert, idx) => (
                      <li key={idx} className="truncate" title={alert}>{alert}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Stepper Horizontal (Frise chronologique) */}
            <div className="w-full lg:w-[350px] shrink-0 flex flex-col justify-center border-t border-workspace pt-6 lg:border-t-0 lg:pt-0 lg:border-l lg:pl-8">
              <div className="relative flex justify-between items-center px-2 mt-2 w-full max-w-sm mx-auto">
                <div className="absolute left-2 right-2 border-t-2 border-dashed border-slate-700 top-1.5 -translate-y-1/2 z-0" />
                <div
                  className="absolute left-2 h-1 bg-gradient-to-r from-success-400 to-success-600 top-1.5 -translate-y-1/2 z-0 transition-all duration-500"
                  style={{ width: `calc(${(currentIndex / (timelineSteps.length - 1)) * 100}% - 16px)` }}
                />
                {timelineSteps.map((step, index) => {
                  const isPast = index < currentIndex;
                  const isCurrent = index === currentIndex;
                  return (
                    <div key={step.id} className="relative z-10 flex flex-col items-center group cursor-help">
                      <div className="relative flex items-center justify-center w-3 h-3">
                        {isCurrent && (
                          <motion.div
                            className="absolute inset-0 rounded-full bg-success-500"
                            animate={{ scale: [1, 1.5, 3.5], opacity: [0, 0.9, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "linear", times: [0, 0.2, 1] }}
                          />
                        )}
                        <div className={`relative z-10 w-full h-full rounded-full ring-4 ring-surface transition-colors duration-300 ${isCurrent ? 'bg-success-600 ring-success-500/40 scale-125' : isPast ? 'bg-success-500' : 'bg-surface border-[2px] border-slate-700'} `} />
                      </div>
                      <span className="absolute bottom-full mb-3 text-[10px] font-medium text-tertiary opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none bg-surface-muted px-2 py-0.5 rounded shadow-sm border border-workspace">
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 flex justify-center">
                {(() => {
                  if (['NEW', 'TO_QUALIFY', 'DOCUMENTS_PENDING'].includes(dossier.status)) {
                    return (
                      <button
                        onClick={async () => {
                          await handleStatusChange('UNDER_ANALYSIS');
                          setActiveTab('analyses');
                        }}
                        disabled={statusLoading}
                        className="btn btn-primary flex items-center justify-center gap-2 h-10 px-5 text-sm font-semibold shadow-sm transition-all w-full max-w-[250px] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {statusLoading ? (
                          <>
                            <LoaderCircle className="w-4 h-4 animate-spin" />
                            Démarrage...
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4" />
                            Démarrer l'Analyse
                          </>
                        )}
                      </button>
                    );
                  }

                  if (dossier.status === 'UNDER_ANALYSIS') {
                    const hasDecideur = !!dossier.decideur;
                    const btn = (
                      <button
                        onClick={() => {
                          if (hasDecideur) {
                            setActiveTab('decision');
                            setDecisionModalState({ isOpen: true, initialType: 'SUBMITTED' });
                          }
                        }}
                        disabled={!hasDecideur}
                        className="btn btn-primary flex items-center justify-center gap-2 h-10 px-5 text-sm font-semibold shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full max-w-[250px]"
                      >
                        <Gavel className="w-4 h-4" />
                        Soumettre au Comité
                      </button>
                    );

                    if (!hasDecideur) {
                      return (
                        <Tooltip content="Veuillez assigner un décideur (onglet Intervenants) avant de soumettre au comité.">
                          <div className="w-full max-w-[250px]">{btn}</div>
                        </Tooltip>
                      );
                    }
                    return btn;
                  }

                  if (dossier.status === 'INVESTMENT_COMMITTEE') {
                    const isDecideur = dossier.decideur?.userId === user?.id;
                    const btn = (
                      <button
                        onClick={() => isDecideur && setActiveTab('decision')}
                        disabled={!isDecideur}
                        className="btn btn-primary flex items-center justify-center gap-2 h-10 px-5 text-sm font-semibold shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full max-w-[250px]"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Rendre la Décision
                      </button>
                    );

                    if (!isDecideur) {
                      return (
                        <Tooltip content={`En attente de la décision de ${dossier.decideur?.Nom || 'décideur'}`}>
                          <div className="w-full max-w-[250px]">{btn}</div>
                        </Tooltip>
                      );
                    }
                    return btn;
                  }

                  if (['APPROVED', 'APPROVED_WITH_CONDITIONS'].includes(dossier.status)) {
                    return (
                      <button
                        type="button"
                        onClick={() => setIsConfirmConvertOpen(true)}
                        disabled={isConverting}
                        className="flex items-center justify-center gap-2 h-10 rounded-xl bg-gradient-to-r from-success-600 to-success-500 px-5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:from-success-700 hover:to-success-600 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 w-full max-w-[250px]"
                      >
                        {isConverting ? (
                          <>
                            <LoaderCircle className="h-4 w-4 animate-spin" strokeWidth={2} />
                            <span>Conversion...</span>
                          </>
                        ) : (
                          <>
                            <ArrowRightLeft className="h-4 w-4" strokeWidth={2} />
                            <span>Structuration</span>
                          </>
                        )}
                      </button>
                    );
                  }

                  return (
                    <div className="text-center mt-3 w-full max-w-sm mx-auto">
                      <p className="text-sm font-semibold text-primary">{timelineSteps[currentIndex]?.label || 'En cours'}</p>
                      <p className="text-[10px] uppercase tracking-wider text-tertiary">Étape actuelle</p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-workspace mb-6">
        <nav className="-mb-px flex space-x-6 sm:space-x-8 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
 whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
 ${activeTab === tab.id
                  ? 'border-secondary text-primary font-semibold'
                  : 'border-transparent text-tertiary hover:text-primary hover:border-workspace'}
 `}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div className="relative min-h-[400px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={tabVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="w-full"
          >
            {activeTab === 'overview' && <DossierOverview dossier={dossier} onRefresh={fetchDossier} />}
            {activeTab === 'participants' && <DossierParticipants dossier={dossier} participants={dossier.participants} onRefresh={fetchDossier} />}
            {activeTab === 'documents' && <DossierDocuments dossierId={dossier.id} documents={dossier.documents} onRefresh={fetchDossier} />}
            {activeTab === 'analyses' && <DossierAnalyses dossier={dossier} onRefresh={fetchDossier} />}
            {activeTab === 'decision' && (
              <DossierDecision
                dossier={dossier}
                onDecisionSaved={fetchDossier}
                externalModalState={decisionModalState}
                setExternalModalState={setDecisionModalState}
              />
            )}
            {activeTab === 'history' && <DossierTimeline history={dossier.history} />}
            {activeTab === 'access' && <ResourceAccess resourceType="DOSSIER" resourceId={dossier.id} />}
          </motion.div>
        </AnimatePresence>
      </div>

      <AlertModal
        isOpen={isConfirmConvertOpen}
        onClose={() => setIsConfirmConvertOpen(false)}
        onConfirm={handleConvert}
        title="Convertir en transaction"
        description="Êtes-vous sûr de vouloir convertir ce dossier du forum en une transaction structurée ? Cette action initiera la procédure de financement."
        confirmText="Convertir"
      />

      <AnimatePresence>
        {convertedTransactionId && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.4, type: "spring", bounce: 0.4 }}
              className="relative bg-surface rounded-2xl shadow-hp-xl border border-workspace p-8 max-w-md w-full flex flex-col items-center text-center z-10"
            >
              <div className="w-16 h-16 bg-success-100 text-success-600 rounded-full flex items-center justify-center mb-6 ring-8 ring-success-50">
                <CheckCircle2 className="w-8 h-8" strokeWidth={2.5} />
              </div>
              <h2 className="text-2xl font-bold text-primary mb-2">Conversion réussie !</h2>
              <p className="text-secondary mb-8 text-sm">
                Ce dossier a été structuré avec succès. Une nouvelle transaction a été créée et le dossier n'apparaîtra plus dans le forum.
              </p>
              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={() => navigate(`/transactions/${convertedTransactionId}`)}
                  className="btn btn-primary w-full py-2.5 text-sm"
                >
                  Aller à la transaction
                </button>
                <button
                  onClick={() => navigate('/forum')}
                  className="btn btn-secondary w-full py-2.5 text-sm bg-surface hover:bg-surface-muted"
                >
                  Retour au forum
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
